<script setup lang="ts">
import { computed, ref } from 'vue';
import { type CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import Modal from '../../Modal.vue';
import useModal, { ModalTypes, modals } from '../../../composables/useModal';
import EditUserInfo from './EditUserInfo.vue';
import { useLocale } from '../../../composables/useLocale';
import { useDeleteUserAssociation } from '../../../composables/useDeleteUserAssociation';

const { t } = useLocale();

const props = defineProps<{
  name: ModalTypes;
  user: CustomerMgmtTypes.GetUserResponse;
  organizationUid: string;
}>();

const emits = defineEmits<{
  (event: 'userUpdated', value: string): void;
}>();

const { isOpen, open, close } = useModal(props.name);
const { open: openDeleteUserModal } = useModal(modals.DELETE_USER);

const user = computed(() => props.user);
const { isUserDeletable } = useDeleteUserAssociation(user);

const formRef = ref();
const isUpdating = ref(false);

const doUpdate = async () => {
  isUpdating.value = true;
  await formRef.value.updateUser().then((result: { success: boolean; updated: boolean }) => {
    if (result.success && result.updated) {
      emits('userUpdated', props.user.uid);
      close();
    }
    if (result.success && !result.updated) {
      close();
    }
  });
  isUpdating.value = false;
};

const onDeleteUser = async () => {
  openDeleteUserModal({ onBack: open });
  close();
};
</script>

<template>
  <Modal
    :name="name"
    :title="t('lcp.edit_user')"
    :is-open="isOpen"
    :show-close-btn="true"
    width="560"
    :action="doUpdate"
    :action-label="t('lcp.save')"
    :loading="isUpdating"
    :secondary-action="onDeleteUser"
    :secondary-action-disabled="!isUserDeletable"
    :secondary-action-label="t('lcp.delete_user')"
    secondary-action-color="error"
  >
    <EditUserInfo v-if="user" ref="formRef" :organization-uid="organizationUid" :user="user" />
  </Modal>
</template>
